"use client";

import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock, ArrowLeft, User, Heart, Eye, MessageSquare, Loader2, Send } from "lucide-react";
import Footer from "./Footer";
import ERPLeadBot from "./ERPLeadBot";
import { API_ENDPOINTS } from "@/constants/config";

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const formatCount = (n) => {
  if (!n) return "0";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return String(n);
};

function renderBlock(block, index) {
  switch (block.type) {
    case "heading":
      return (
        <h2 key={index} className="text-3xl font-bold text-gray-900 mt-12 mb-6 first:mt-0">
          {block.text}
        </h2>
      );
    case "subheading":
      return (
        <h3 key={index} className="text-xl font-semibold text-gray-800 mt-8 mb-4">
          {block.text}
        </h3>
      );
    case "quote":
      return (
        <blockquote key={index} className="border-l-4 border-blue-500 pl-6 my-6 italic text-gray-600 text-lg">
          {block.text}
        </blockquote>
      );
    case "list":
      return (
        <ul key={index} className="list-disc list-inside space-y-2 my-6 text-gray-700 text-lg">
          {(block.items || []).map((item, i) => (
            <li key={i} className="leading-relaxed">{item}</li>
          ))}
        </ul>
      );
    default:
      return (
        <p key={index} className="text-gray-700 leading-relaxed mb-6 text-lg">
          {block.text}
        </p>
      );
  }
}

function AuthorCard({ author, authorDesignation, authorAvatar }) {
  if (!author) return null;
  return (
    <div className="flex items-center gap-4 p-6 bg-gray-50 rounded-xl border mt-10">
      {authorAvatar ? (
        <img src={authorAvatar} alt={author} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
          <User className="w-7 h-7 text-blue-500" />
        </div>
      )}
      <div>
        <p className="font-semibold text-gray-900 text-base">{author}</p>
        {authorDesignation && <p className="text-sm text-gray-500 mt-0.5">{authorDesignation}</p>}
      </div>
    </div>
  );
}

function LikeButton({ slug, initialLikes }) {
  const likedKey = `blog_liked_${slug}`;
  const [liked, setLiked] = useState(() => localStorage.getItem(likedKey) === "true");
  const [count, setCount] = useState(initialLikes ?? 0);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (loading) return;
    setLoading(true);
    const endpoint = liked ? API_ENDPOINTS.publicBlogUnlike(slug) : API_ENDPOINTS.publicBlogLike(slug);
    try {
      const res = await fetch(endpoint, { method: "POST" });
      if (!res.ok) {
        console.error("[like] API error:", res.status, res.statusText);
        return;
      }
      const json = await res.json();
      if (json?.data?.likes !== undefined) {
        setCount(json.data.likes);
        const next = !liked;
        setLiked(next);
        localStorage.setItem(likedKey, String(next));
      }
    } catch (err) {
      console.error("[like] request failed:", err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={toggle}
      disabled={loading}
      className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all select-none
        ${liked
          ? "bg-red-50 border-red-200 text-red-500 hover:bg-red-100"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-200 hover:text-red-400"
        } disabled:opacity-60`}
      aria-label={liked ? "Unlike" : "Like"}
    >
      <Heart className={`w-4 h-4 transition-all ${liked ? "fill-red-500 text-red-500" : ""}`} />
      <span>{formatCount(count)}</span>
    </button>
  );
}

const COMMENT_LIMIT = 10;

function CommentsSection({ slug }) {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingComments, setLoadingComments] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);

  const fetchComments = async (pageNum, append = false) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.publicBlogComments(slug)}?page=${pageNum}&limit=${COMMENT_LIMIT}`);
      const json = await res.json();
      if (json?.data) {
        setComments((prev) => append ? [...prev, ...json.data.comments] : json.data.comments);
        setTotal(json.data.total);
      }
    } catch {
      // silently ignore
    }
  };

  useEffect(() => {
    setLoadingComments(true);
    fetchComments(1).finally(() => setLoadingComments(false));
  }, [slug]);

  const loadMore = async () => {
    setLoadingMore(true);
    const next = page + 1;
    await fetchComments(next, true);
    setPage(next);
    setLoadingMore(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!name.trim()) { setSubmitError("Name is required"); return; }
    if (!body.trim()) { setSubmitError("Comment is required"); return; }

    setSubmitting(true);
    try {
      const res = await fetch(API_ENDPOINTS.publicBlogComments(slug), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), body: body.trim() }),
      });
      const json = await res.json();
      if (!res.ok) {
        setSubmitError(json?.message || "Failed to submit comment");
        return;
      }
      setSubmitted(true);
      setName("");
      setBody("");
      // prepend new comment
      if (json?.data?.comment) {
        setComments((prev) => [json.data.comment, ...prev]);
        setTotal((prev) => prev + 1);
      }
    } catch {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const hasMore = comments.length < total;

  return (
    <div className="mt-12 pt-10 border-t">
      <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
        <MessageSquare className="w-6 h-6 text-blue-500" />
        Comments
        {total > 0 && <span className="text-base font-normal text-gray-400">({total})</span>}
      </h3>

      {/* Comment Form */}
      <div className="bg-gray-50 rounded-xl p-6 mb-8 border">
        <h4 className="font-semibold text-gray-800 mb-4">Leave a Comment</h4>
        {submitted && (
          <p className="text-green-600 text-sm mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
            Comment posted successfully!
          </p>
        )}
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={100}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Comment <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Share your thoughts..."
              rows={4}
              maxLength={2000}
              disabled={submitting}
              className="resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{body.length}/2000</p>
          </div>
          {submitError && (
            <p className="text-red-500 text-sm">{submitError}</p>
          )}
          <Button type="submit" disabled={submitting} className="gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? "Posting..." : "Post Comment"}
          </Button>
        </form>
      </div>

      {/* Comments List */}
      {loadingComments ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No comments yet. Be the first to share your thoughts!</p>
      ) : (
        <div className="space-y-6">
          {comments.map((c) => (
            <div key={c._id} className="flex gap-4">
              <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-blue-600 font-semibold text-sm">{c.name?.[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 bg-white border rounded-xl px-5 py-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-900 text-sm">{c.name}</span>
                  <span className="text-xs text-gray-400">{formatDate(c.createdAt)}</span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{c.body}</p>
              </div>
            </div>
          ))}

          {hasMore && (
            <div className="text-center pt-2">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore} className="gap-2">
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                Load more comments
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const fetchPost = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.publicBlog(slug));
        if (res.status === 404) { setNotFound(true); return; }
        const json = await res.json();
        if (json?.data?.blog) {
          setPost(json.data.blog);
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/blog">
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white">
      <article>
        {/* Header */}
        <section className="relative py-12 bg-gradient-to-br from-blue-50 via-white to-blue-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>

            <div className="mb-4">
              <Badge>{post.category}</Badge>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">{post.title}</h1>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">{post.excerpt}</p>

            <div className="flex items-center gap-5 text-sm text-gray-500 flex-wrap">
              {post.author && (
                <div className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  <span>{post.author}</span>
                  {post.authorDesignation && (
                    <span className="text-gray-400">· {post.authorDesignation}</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(post.publishedAt)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
              {post.views > 0 && (
                <div className="flex items-center gap-1.5">
                  <Eye className="w-4 h-4" />
                  <span>{formatCount(post.views)} views</span>
                </div>
              )}
            </div>

            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {post.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Cover Image */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 mb-8">
          <img
            src={post.coverImage || "/everythingknow.png"}
            alt={post.title}
            className="w-full max-h-[420px] object-contain rounded-xl shadow-md"
          />
        </div>

        {/* Content */}
        <section className="py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose max-w-none">
              {(post.content || []).map((block, index) => renderBlock(block, index))}
            </div>

            {/* Author card */}
            <AuthorCard
              author={post.author}
              authorDesignation={post.authorDesignation}
              authorAvatar={post.authorAvatar}
            />

            {/* Like button */}
            <div className="flex items-center gap-3 mt-8 pt-8 border-t">
              <span className="text-sm text-gray-500">Did you find this article helpful?</span>
              <LikeButton slug={post.slug} initialLikes={post.likes} />
            </div>

            {/* Comments */}
            <CommentsSection slug={post.slug} />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gray-50 border-t">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Ready to Transform Your School Management?</h3>
            <p className="text-xl text-gray-600 mb-8">
              Discover how Clynox can help streamline your institution's operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="px-8">Start Free Trial</Button>
              </Link>
              <Link to="/features">
                <Button variant="outline" size="lg" className="px-8">Explore Features</Button>
              </Link>
            </div>
          </div>
        </section>
      </article>

      <Footer />
      <ERPLeadBot />
    </div>
  );
}
